<template>
  <v-dialog v-model="dialog" scrollable max-width="900">
    <v-card>
      <div class="fdlg__form">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">Istoricul echipamentului</h2>
            <p class="fdlg__sub">{{ subtitle }}</p>
          </div>
          <v-btn icon aria-label="Închide" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <p class="eqh__note">
            <v-icon small>mdi-information-outline</v-icon>
            <span>
              Istoricul urmează echipamentul fizic și după transferuri, oricine i-ar fi responsabil.
              Demersurile păstrează datele echipamentului din ziua depunerii: modificările de aici nu le schimbă.
            </span>
          </p>

          <div v-if="loading" class="eqh__loading">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="errorMessage" class="eqh__empty eqh__empty--error">
            <v-icon>mdi-alert-circle-outline</v-icon>{{ errorMessage }}
          </div>

          <template v-else>
            <section class="form-section">
              <div class="form-section__label">
                <v-icon small>mdi-timeline-clock-outline</v-icon>Modificări
                <span class="eqh__count">{{ changes.length }}</span>
              </div>
              <div v-if="!changes.length" class="eqh__empty">
                <v-icon>mdi-information-outline</v-icon>
                Nicio modificare înregistrată. Jurnalul ține modificările de la introducerea lui; cele mai vechi nu apar aici.
              </div>
              <ol v-else class="eqh__trail">
                <li
                  v-for="entry in changes"
                  :key="entry.id"
                  class="eqh__step"
                  :class="'tone-' + actionOf(entry).tone"
                >
                  <span class="eqh__dot" aria-hidden="true">
                    <v-icon x-small>{{ actionOf(entry).icon }}</v-icon>
                  </span>
                  <div class="eqh__body">
                    <div class="eqh__title">
                      <span class="eqh__action">{{ actionOf(entry).label }}</span>
                      <span v-if="entry.requestId" class="eqh__req">
                        <v-icon x-small>mdi-file-document-outline</v-icon>{{ requestLabel(entry.requestId) }}
                      </span>
                    </div>
                    <div class="eqh__meta">{{ entry.user || "—" }} · {{ formatDate(entry.date) }}</div>
                    <ul v-if="entry.changes && entry.changes.length" class="eqh__fields">
                      <li v-for="(change, i) in entry.changes" :key="i">
                        <span class="eqh__field">{{ fieldLabel(change.field) }}</span>
                        <span class="eqh__old">{{ showValue(change.field, change.from) }}</span>
                        <v-icon small class="eqh__arrow" aria-label="devine">mdi-arrow-right</v-icon>
                        <span class="eqh__new">{{ showValue(change.field, change.to) }}</span>
                      </li>
                    </ul>
                  </div>
                </li>
              </ol>
            </section>

            <section class="form-section">
              <div class="form-section__label">
                <v-icon small>mdi-file-document-multiple-outline</v-icon>Demersuri pe acest echipament
                <span class="eqh__count">{{ requests.length }}</span>
              </div>
              <div v-if="!requests.length" class="eqh__empty">
                <v-icon>mdi-information-outline</v-icon>
                Nu au fost depuse demersuri pentru acest echipament.
              </div>
              <v-data-table
                v-else
                :headers="requestHeaders"
                :items="requests"
                :items-per-page="-1"
                :mobile-breakpoint="600"
                hide-default-footer
                disable-sort
                class="eqh__table"
              >
                <template v-slot:item.approach_number="{ item }">
                  {{ item.approach_number || "—" }}
                </template>
                <template v-slot:item.date="{ item }">
                  <span class="eqh__nowrap">{{ formatDate(item.date) }}</span>
                </template>
                <template v-slot:item.user="{ item }">
                  <div>{{ item.user || "—" }}</div>
                  <div v-if="item.department" class="eqh__sub">{{ item.department }}</div>
                </template>
                <template v-slot:item.status="{ item }">
                  <StatusChip :status="item.status" small />
                </template>
              </v-data-table>
            </section>
          </template>
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer></v-spacer>
          <v-btn text large @click="dialog = false">Închide</v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import StatusChip from "@/components/elements/StatusChip.vue";

const ACTIONS = {
  created: { label: "Adăugat", icon: "mdi-plus", tone: "green" },
  edited: { label: "Modificat", icon: "mdi-pencil", tone: "sky" },
  transferred: { label: "Transferat", icon: "mdi-swap-horizontal", tone: "plum" },
  activated: { label: "Activat la livrare", icon: "mdi-package-variant-closed-check", tone: "teal" },
  "request-edit": { label: "Modificat prin demers", icon: "mdi-file-document-edit", tone: "amber" },
};

const FIELDS = {
  equipment_type_id: "Tip",
  model: "Model",
  registration_number: "Nr. inventar",
  responsible_person_id: "Responsabil",
  location_id: "Locația",
  location: "Locația",
  department_id: "Departament",
  isFunctional: "Este activ",
  isActive: "Activ",
  serial_number: "Seria",
  location_details: "Detalii amplasare",
  label_file_id: "Fotografie etichetă",
  place_file_id: "Fotografie amplasare",
  // an item added by the Excel import: the file's name
  import_file: "Importat din fișierul",
};

// Change log of one physical item (GET /equipments/:id/history) and the requests filed on it
export default {
  name: "EquipmentHistoryDialog",
  components: { StatusChip },
  data() {
    return {
      dialog: false,
      item: null,
      loading: false,
      errorMessage: "",
      changes: [],
      requests: [],
      requestHeaders: [
        { text: "Nr. demers", value: "approach_number", width: "90px" },
        { text: "Cauza", value: "cause" },
        { text: "Data", value: "date" },
        { text: "Cine", value: "user" },
        { text: "Starea", value: "status" },
      ],
    };
  },
  computed: {
    subtitle() {
      const it = this.item;
      if (!it) return "";
      const name = [it.equipmentType, it.model].filter(Boolean).join(" ");
      const parts = [name];
      if (it.registration_number) parts.push(`nr. inventar ${it.registration_number}`);
      if (it.responsable_person) parts.push(it.responsable_person);
      return parts.filter(Boolean).join(", ");
    },
  },
  methods: {
    async show(item) {
      this.item = item;
      this.changes = [];
      this.requests = [];
      this.errorMessage = "";
      this.dialog = true;
      helper.scrollToTop(this);
      this.loading = true;
      try {
        const data = await api.getEquipmentHistory(item.id);
        // a newer click may have opened another item meanwhile
        if (this.item !== item) return;
        this.changes = data.changes || [];
        this.requests = data.requests || [];
      } catch (error) {
        console.error("Error fetching equipment history:", error);
        this.errorMessage = error.response?.data?.message || "Istoricul nu a putut fi încărcat. Încercați din nou.";
      } finally {
        if (this.item === item) this.loading = false;
      }
    },
    actionOf(entry) {
      const action = ACTIONS[entry.action] || { label: entry.action, icon: "mdi-circle-small", tone: "slate" };
      // an item created by a new-item request
      if (entry.action === "created" && entry.requestId) return { ...action, label: "Adăugat prin demers" };
      return action;
    },
    fieldLabel(field) {
      return FIELDS[field] || field;
    },
    // values come readable from the server; flags may still arrive as true / false / 1 / 0
    showValue(field, value) {
      if (value === null || value === undefined || value === "") return "—";
      if (field === "isActive" || field === "isFunctional") {
        const text = String(value).toLowerCase();
        if (["true", "1", "da"].includes(text)) return "Da";
        if (["false", "0", "nu"].includes(text)) return "Nu";
      }
      return String(value);
    },
    requestLabel(requestId) {
      const request = this.requests.find((r) => r.id === requestId);
      if (!request) return "prin demers";
      if (request.approach_number) return `Demersul nr. ${request.approach_number}`;
      return `Demersul „${request.cause}” din ${this.formatDate(request.date, false)}`;
    },
    // "2026-09-29 08:40:03" (server local time) or an ISO date -> "29.09.2026 08:40"
    formatDate(value, withTime = true) {
      if (!value) return "—";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date)) return String(value);
      const pad = (n) => String(n).padStart(2, "0");
      const day = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
      return withTime ? `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}` : day;
    },
  },
};
</script>

<style scoped>
.eqh__note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  padding: 12px 14px;
  border: 1px solid var(--leaf-200);
  border-radius: 12px;
  background: var(--mint-50);
  color: var(--ink-2);
}
.eqh__note .v-icon {
  color: var(--green);
  margin-top: 2px;
}
.eqh__loading {
  display: flex;
  justify-content: center;
  padding: 32px 0;
}
.eqh__empty {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  border: 1px dashed var(--line);
  border-radius: 10px;
  background: var(--mint-50);
  color: var(--ink-2);
}
.eqh__empty .v-icon {
  color: var(--green);
}
.eqh__empty--error {
  border-color: var(--brick);
  background: var(--brick-bg);
  color: var(--brick);
}
.eqh__empty--error .v-icon {
  color: var(--brick);
}
.eqh__count {
  padding: 1px 9px;
  border-radius: 999px;
  background: var(--mint-50);
  border: 1px solid var(--line);
  font-family: var(--font-ui);
  font-size: 0.85rem;
  color: var(--ink-2);
}

/* timeline, newest first */
.eqh__trail {
  list-style: none;
  margin: 0;
  padding: 2px 0 6px;
}
.eqh__step {
  position: relative;
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 12px;
  padding-bottom: 16px;
}
.eqh__step:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 11px;
  top: 26px;
  bottom: 0;
  width: 2px;
  background: var(--line);
}
.eqh__dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: currentColor;
}
.eqh__dot .v-icon {
  color: #fff !important;
}
.tone-green { color: var(--green); }
.tone-sky { color: var(--sky); }
.tone-plum { color: var(--plum); }
.tone-teal { color: var(--teal); }
.tone-amber { color: var(--amber); }
.tone-slate { color: var(--slate); }
.eqh__body {
  min-width: 0;
  color: var(--ink);
}
.eqh__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
}
.eqh__action {
  font-weight: 700;
}
.tone-green .eqh__action { color: var(--green-700); }
.tone-sky .eqh__action { color: var(--sky); }
.tone-plum .eqh__action { color: var(--plum); }
.tone-teal .eqh__action { color: var(--teal); }
.tone-amber .eqh__action { color: var(--amber); }
.eqh__req {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 9px;
  border-radius: 999px;
  background: var(--slate-bg);
  color: var(--ink-2);
  font-size: 0.82rem;
  font-weight: 600;
}
.eqh__req .v-icon {
  color: var(--ink-3);
}
.eqh__meta {
  margin-top: 2px;
  font-size: 0.88rem;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
.eqh__fields {
  list-style: none;
  margin: 8px 0 0;
  padding: 8px 12px;
  border: 1px solid var(--line-soft);
  border-radius: 10px;
  background: var(--mint-50);
}
.eqh__fields li {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
  padding: 3px 0;
  color: var(--ink);
}
.eqh__field {
  min-width: 110px;
  color: var(--ink-3);
  font-size: 0.92rem;
}
.eqh__old {
  color: var(--ink-3);
  text-decoration: line-through;
  text-decoration-color: rgba(95, 112, 104, 0.5);
  word-break: break-word;
}
.eqh__arrow.v-icon {
  color: var(--ink-3);
  align-self: center;
}
.eqh__new {
  font-weight: 700;
  word-break: break-word;
}

.eqh__table {
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 12px;
}
.eqh__sub {
  font-size: 0.85rem;
  color: var(--ink-3);
}
.eqh__nowrap {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
</style>
